import { PERSONALIZATION_VARIABLES } from '@/lib/constants/personalization-variables';
import { processEmailHtml } from '@/lib/utils/email-html-processor';

export interface ContactData {
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  linkedinUrl?: string;
  senderFirstName?: string;
  senderLastName?: string;
}

const DEFAULT_CONTACT: Required<ContactData> = {
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  company: 'Acme Corp',
  linkedinUrl: 'https://linkedin.com/in/johndoe',
  senderFirstName: 'Jane',
  senderLastName: 'Smith',
};

/**
 * Replaces personalization variables in content with contact data.
 * Also processes HTML to add inline styles for consistent email rendering.
 * Falls back to default values if contact data is missing.
 */
export function replaceVariables(content: string, contact?: ContactData): string {
  if (!content) return content;

  const data: Required<ContactData> = {
    firstName: contact?.firstName || DEFAULT_CONTACT.firstName,
    lastName: contact?.lastName || DEFAULT_CONTACT.lastName,
    email: contact?.email || DEFAULT_CONTACT.email,
    company: contact?.company || DEFAULT_CONTACT.company,
    linkedinUrl: contact?.linkedinUrl || DEFAULT_CONTACT.linkedinUrl,
    senderFirstName: contact?.senderFirstName || DEFAULT_CONTACT.senderFirstName,
    senderLastName: contact?.senderLastName || DEFAULT_CONTACT.senderLastName,
  };

  let result = content;

  // Replace personalization variables
  PERSONALIZATION_VARIABLES.forEach((variable) => {
    const placeholder = variable.placeholder;
    const value = data[variable.key as keyof ContactData] || '';
    result = result.replace(new RegExp(escapeRegex(placeholder), 'g'), value);
  });

  // Replace {AI instruction} blocks with styled placeholder for preview
  const AI_INSTRUCTION_REGEX = /(?<!\{)\{(?!\{)([^{}]+)\}(?!\})/g;
  result = result.replace(AI_INSTRUCTION_REGEX, '<span style="background-color: rgba(147, 51, 234, 0.15); border-radius: 2px; padding: 1px 3px; font-style: italic;">AI: $1</span>');

  // Process HTML to add inline styles for consistent rendering
  result = processEmailHtml(result);

  return result;
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export { DEFAULT_CONTACT };
